import React, { FC, useEffect, useMemo, useState } from 'react'
import { Transition } from 'react-transition-group'

// Utils
import { getItem, setItem } from 'utils/localStorage'

// Constants
import localStorageKeys from 'constants/localStorageKeys'

// Icons
import { ReactComponent as CloseIcon } from 'icons/cross-20.svg'

// Components
import Pill from 'components/Pill'
import { AnchorButton } from 'components/Button'

// Styles
import {
  Container,
  Panel,
  Content,
  Header,
  Title,
  Footer,
  CloseButton,
  CopyContainer,
  Copy,
  OpenButton,
} from './PageCanvasFeaturePreview.styles'
import { CANVAS_VIEW_BETA_DOCS, CANVAS_VIEW_FEEDBACK } from '../../constants'

// Helpers
const localStorageKey = localStorageKeys['canvasBetaFeaturePreviewClosed']

/**
 * Renders an animated feature preview panel, that can be opened and closed.
 *
 * The first time the user encounters this component it is in its opened state,
 * thereafter it'll always be initially closed.
 */
export const PageCanvasFeaturePreview: FC = () => {
  const closedInitial = useMemo(() => getItem(localStorageKey) === '1', [])
  const [closed, setClosed] = useState(closedInitial)

  useEffect(() => {
    setItem(localStorageKey, '1')
  }, [])

  return (
    <Transition in={!closed} timeout={300}>
      {state => (
        <Container state={state}>
          <Panel state={state} />
          <Content state={state}>
            <Header>
              <Title state={state}>
                {state === 'entered' || state === 'entering'
                  ? 'Canvas View'
                  : 'Canvas view in'}
              </Title>
              <Pill variant={{ kind: 'secondary' }}>Beta</Pill>
              <CloseButton state={state} onClick={() => setClosed(true)}>
                <CloseIcon width="12px" />
              </CloseButton>
            </Header>
            <CopyContainer state={state}>
              <Copy>
                Scroll in any direction, zoom in or out, and double-click to
                focus on a board.
              </Copy>
              <Copy>
                While in beta, some elements may not display as expected.
              </Copy>
            </CopyContainer>
            <Footer state={state}>
              <AnchorButton
                href={CANVAS_VIEW_BETA_DOCS}
                target="_blank"
                variant="primary">
                Learn More
              </AnchorButton>
              <AnchorButton
                href={CANVAS_VIEW_FEEDBACK}
                target="_blank"
                variant="secondary">
                Send Feedback
              </AnchorButton>
            </Footer>
          </Content>
          <OpenButton state={state} onClick={() => setClosed(false)} />
        </Container>
      )}
    </Transition>
  )
}
